import {
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Icon,
  Text,
  Box,
} from "@chakra-ui/react";
import { MdEvStation } from "react-icons/md";
import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { useRecoilState, useRecoilValue } from "recoil";
import useAxios from "axios-hooks";
import { useLocation, useNavigate } from "react-router-dom";

import { createChargingPointDrawerState } from "atoms/createChargingPointDrawerState";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import ChargePoint from "../tab_panels/ChargePoint";
import CreateChargePointDrawer from "../components/CreateChargePointDrawer";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import Card from "components/card/Card";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";

const ChargePointTabs = ({ id }) => {
  const [chargePoints, setChargePoints] = useState([]);
  const [drawerState, setDrawerState] = useRecoilState(
    createChargingPointDrawerState,
  );
  const [currentTab, setCurrentTab] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const [{ data, loading, error }, getChargePoint] = useAxios(
    { method: "GET", url: `/api/stations/forLocation/${id}` },
    { manual: true },
  );

  const getChargePoints = async () => {
    try {
      const response = await getChargePoint();
      setChargePoints(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const refreshHandler = async () => {
    setDrawerState(null);
    await getChargePoints();
  };

  const chargePointTabs = chargePoints.map((ele, index) => {
    return (
      <Tab
        key={index}
        mx={1}
        borderRadius="lg"
        px={{ base: 2, md: 3 }}
        py={2}
        fontWeight="500"
        color="gray.600"
        position="relative"
        zIndex="1"
        minW="fit-content"
        whiteSpace="nowrap"
        _selected={{
          bg: "rgba(35, 169, 121, 0.15)",
          color: "brand.500",
          fontWeight: "600",
          boxShadow: "0 2px 4px rgba(35, 169, 121, 0.1)",
        }}
        _hover={{
          bg: "rgba(35, 169, 121, 0.08)",
          color: "brand.500",
        }}
      >
        <Flex align="center" gap={{ base: 1, md: 1.5 }}>
          <Icon as={MdEvStation} boxSize={{ base: 3.5, md: 4 }} />
          <Text fontSize={{ base: "sm", md: "md" }}>{ele.StationId}</Text>
        </Flex>
      </Tab>
    );
  });

  const chargePointPanels = chargePoints.map((ele, index) => {
    return (
      <TabPanel key={index}>
        <ChargePoint getChargePoints={getChargePoints} data={ele} />
      </TabPanel>
    );
  });

  // Syncing the URL with the selected charge point tab
  const handleTabChange = (index) => {
    const selectedChargePoint = chargePoints[index];
    setCurrentTab(index);
    navigate(`?chargepoint=${selectedChargePoint.StationId}`, {
      replace: true,
    });
  };

  useEffect(() => {
    getChargePoints();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    console.log(searchParams);
    const stationId = searchParams.get("sid");
    if (chargePoints.length > 0 && stationId) {
      const index = chargePoints.findIndex(
        (chargePoint) => chargePoint.StationId === stationId,
      );

      if (index !== -1) {
        setCurrentTab(index);
      } else {
        navigate(-1); // If chargerId in URL doesn't match, go back.
      }
    }
  }, [chargePoints, location.search, navigate]);

  return !loading ? (
    <Card
      minH="60vh"
      height="100%"
      px={{ base: 2, md: 4 }}
      py={{ base: 3, md: 4 }}
    >
      {chargePoints.length == 0 && (
        <Flex
          py={{ base: 4, md: 6 }}
          px={{ base: 2, md: 4 }}
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              colorScheme="whatsapp"
              variant="solid"
              onClick={() => setDrawerState({ isOpen: true })}
              disabled={!canEdit}
              size={{ base: "md", md: "lg" }}
            >
              Add Charger
            </Button>
          </RoleBasedRender>
        </Flex>
      )}
      {drawerState?.isOpen && (
        <CreateChargePointDrawer
          refresh={getChargePoints}
          id={id}
          update={refreshHandler}
        />
      )}

      {chargePoints.length > 0 && (
        <Tabs
          index={currentTab}
          onChange={handleTabChange} // Set the tab change handler
          isLazy
          variant="unstyled"
        >
          <Flex
            flexDirection={{ base: "column", sm: "row" }}
            alignItems={{ base: "stretch", sm: "start" }}
            justifyContent="space-between"
            gap={{ base: 3, sm: 0 }}
          >
            <Box
              bg="rgba(255, 255, 255, 0.6)"
              backdropFilter="blur(12px)"
              borderRadius="xl"
              border="1px solid rgba(35, 169, 121, 0.15)"
              px={1}
              py={2}
              position="relative"
              overflow="hidden"
              overflowX={{ base: "auto", sm: "hidden" }}
              w={{ base: "100%", sm: "auto" }}
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "100%",
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              <TabList gap={2} flexWrap={{ base: "nowrap", sm: "wrap" }}>
                {chargePointTabs}
              </TabList>
            </Box>
            <RoleBasedRender roles={onlyAdmins}>
              <Button
                leftIcon={<FaPlus />}
                colorScheme="whatsapp"
                variant="solid"
                onClick={() => setDrawerState({ isOpen: true })}
                disabled={!canEdit}
                w={{ base: "100%", sm: "auto" }}
                fontSize={{ base: "sm", md: "md" }}
              >
                Add Charger
              </Button>
            </RoleBasedRender>
          </Flex>

          <TabPanels>{chargePointPanels}</TabPanels>
        </Tabs>
      )}
    </Card>
  ) : (
    <LoadingSpinner />
  );
};

export default ChargePointTabs;
